import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { MailingContext } from '../../../../contexts/MailingContext';
import { ProfileContext } from '../../../../contexts/ProfileContext';
import { showSuccessMessage, showErrorMessage } from '../../../../helpers/useToast';

const MailingStatsReport = () => {
  const { getResources, getAllInventoryResources } = useContext(MailingContext);
  const { worker } = useContext(ProfileContext);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const loadAndProcessReportData = async () => {
    const resourcesResponse = await getResources('completed', 1, {
      start_date: startDate,
      end_date: endDate,
      per_page: 100,
    });

    // Full inventory list (avoids page-1 truncation)
    const allInventory = await getAllInventoryResources();

    return processMailingData(
      resourcesResponse.data || [],
      allInventory || [],
      startDate,
      endDate
    );
  };

  const fetchReportData = async () => {
    if (!startDate || !endDate) {
      showErrorMessage('Please select both start and end dates');
      return;
    }

    setLoading(true);
    try {
      const processedData = await loadAndProcessReportData();
      setReportData(processedData);
    } catch (error) {
      console.error('Error fetching report data:', error);
      showErrorMessage('Failed to fetch report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!startDate || !endDate) {
      showErrorMessage('Please select both start and end dates');
      return;
    }

    setLoading(true);
    try {
      const processedData = await loadAndProcessReportData();
      setReportData(processedData);
      generatePDF(processedData);
    } catch (error) {
      console.error('Error generating report:', error);
      showErrorMessage('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processMailingData = (resources, inventory, startDate, endDate) => {
    // Count brochures by category
    const brochureCounts = {};
    const letterCounts = {};
    const packageCounts = {
      canada: 0,
      usa: 0
    };
    const bookCounts = {};
    const materialCounts = {
      labels: 0,
      bubbleEnvelopes: 0,
      regularEnvelopes: 0,
      paperSheets: 0
    };
    let externalBookCount = 0;

    // In-house = physical inventory with distributor "internal"
    // Map lowercase key -> canonical inventory title for case-insensitive matching
    const inHouseTitleByKey = new Map();
    (inventory || []).forEach((item) => {
      const title = (item.resource || item.title || '').trim();
      if (
        item.type === 'physical' &&
        item.distributor === 'internal' &&
        title
      ) {
        const key = title.toLowerCase();
        if (!inHouseTitleByKey.has(key)) {
          inHouseTitleByKey.set(key, title);
        }
      }
    });

    // Process resources
    resources.forEach(resource => {
      // Process brochures from the brochures field
      if (resource.brochures) {
        const brochures = resource.brochures.split(',').map(b => b.trim());
        brochures.forEach(brochure => {
          // Count brochures by their actual titles (dynamic)
          if (brochure && brochure.length > 0) {
            brochureCounts[brochure] = (brochureCounts[brochure] || 0) + 1;
          }
        });
      }

      // Process books from the title field — in-house only
      // Anchor / Give Cloud / unmatched titles are excluded from book counts
      if (resource.title) {
        const titles = resource.title.split(',').map(t => t.trim()).filter(Boolean);
        titles.forEach(title => {
          const canonical = inHouseTitleByKey.get(title.toLowerCase());
          if (canonical) {
            bookCounts[canonical] = (bookCounts[canonical] || 0) + 1;
          } else {
            externalBookCount++;
          }
        });
      }

      // Count letters from the printouts field
      if (resource.printouts) {
        const printouts = resource.printouts.split(',').map(p => p.trim());
        printouts.forEach(printout => {
          // Count letters by their actual titles (dynamic)
          if (printout && printout.length > 0) {
            letterCounts[printout] = (letterCounts[printout] || 0) + 1;
          }
        });
      }

      // Count packages by country (simplified logic)
      if (resource.user_number) {
        if (resource.user_number.startsWith('+1') && resource.user_number.length === 12) {
          packageCounts.canada++;
        } else {
          packageCounts.usa++;
        }
      }

      // Count materials
      materialCounts.labels++;
      materialCounts.regularEnvelopes++;
      materialCounts.paperSheets += 2; // Estimate 2 sheets per package
    });

    return {
      dateRange: { start: startDate, end: endDate },
      brochureCounts,
      letterCounts,
      packageCounts,
      bookCounts,
      materialCounts,
      externalBookCount,
      totalBrochures: Object.values(brochureCounts).reduce((sum, count) => sum + count, 0),
      totalBooks: Object.values(bookCounts).reduce((sum, count) => sum + count, 0),
      totalPackages: packageCounts.canada + packageCounts.usa
    };
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const maxY = pageHeight - margin;
    
    let yPosition = 20;
    const pageWidth = doc.internal.pageSize.width;
    
    // Helper function to check if we need a new page
    const checkPageBreak = (requiredSpace = 10) => {
      if (yPosition + requiredSpace > maxY) {
        doc.addPage();
        yPosition = 20;
        return true;
      }
      return false;
    };
    
    // Helper function to add text with automatic page break and wrapping
    const addText = (text, x, fontSize = 10, fontStyle = 'normal', spacing = 8) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', fontStyle);
      const maxWidth = pageWidth - x - margin;
      const lines = doc.splitTextToSize(String(text), maxWidth);
      const lineHeight = Math.max(fontSize * 0.45, 6);
      const blockHeight =
        lines.length > 1
          ? (lines.length - 1) * lineHeight + spacing
          : spacing;
      checkPageBreak(blockHeight);
      doc.text(lines, x, yPosition);
      yPosition += blockHeight;
    };

    // Label + count row: wrap long titles so they never overlap the count column
    const labelX = 14;
    const countX = pageWidth - margin;
    const labelMaxWidth = countX - labelX - 12;

    const addLabelCountRow = (label, count) => {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(String(label), labelMaxWidth);
      const rowHeight = Math.max(8, lines.length * 8);
      checkPageBreak(rowHeight);
      doc.text(lines, labelX, yPosition);
      doc.text(String(count), countX, yPosition, { align: 'right' });
      yPosition += rowHeight;
    };
    
    // Title
    addText('Mail Out Stats', 14, 18, 'bold', 20);
    
    // Date range
    const startDateFormatted = new Date(data.dateRange.start).toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
    const endDateFormatted = new Date(data.dateRange.end).toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
    
    addText(`${startDateFormatted} – ${endDateFormatted}`, 14, 12, 'normal', 8);
    addText(`${worker?.fullName || 'User'}/Prayer Centre/Mailings/`, 14, 12, 'normal', 8);
    //addText('*A1P & Home Pastoral Follow Up Names are all mixed in this report.', 14, 12, 'normal', 15);

    // Brochures section
    addText('Brochures:', 14, 14, 'bold', 10);
    
    Object.entries(data.brochureCounts).forEach(([category, count]) => {
      if (count > 0) {
        addLabelCountRow(category, count);
      }
    });

    addText(`Combined Total: ${data.totalBrochures} brochures`, 14, 10, 'normal', 15);

    // Letters section
    addText('Letters:', 14, 14, 'bold', 10);
    
    Object.entries(data.letterCounts).forEach(([letter, count]) => {
      if (count > 0) {
        addLabelCountRow(letter, count);
      }
    });

    // Packages section
    addText('Packages:', 14, 14, 'bold', 10);
    addText(`Canadian Mailed Packages: ${data.packageCounts.canada}`, 14, 10, 'normal', 8);
    addText(`USA Mailed Packages: ${data.packageCounts.usa}`, 14, 10, 'normal', 15);

    // Books section
    addText('Books (In-House stock):', 14, 14, 'bold', 10);
    
    Object.entries(data.bookCounts).forEach(([book, count]) => {
      if (count > 0) {
        addLabelCountRow(book, count);
      }
    });

    addText(
      `Total of Books: ${data.totalBooks} books mailed (inhouse titles only)`,
      14,
      10,
      'normal',
      data.externalBookCount > 0 ? 8 : 20
    );
    if (data.externalBookCount > 0) {
      addText(
        `Excluded (Anchor / Give Cloud / unmatched): ${data.externalBookCount}`,
        14,
        10,
        'normal',
        20
      );
    }

    // Materials section
    addText('Materials Used:', 14, 14, 'bold', 10);
    addText(`Labels printed: ${data.materialCounts.labels}`, 14, 10, 'normal', 8);
    addText(`Bubble envelope(s) 9x12 used: ${data.materialCounts.bubbleEnvelopes}`, 14, 10, 'normal', 8);
    addText(`Regular 9x12 envelope(s) used: ${data.materialCounts.regularEnvelopes}`, 14, 10, 'normal', 8);
    addText(`Total copy paper used: ${data.materialCounts.paperSheets} sheets`, 14, 10, 'normal', 20);

    // Footer
    addText('**Anchor / Give Cloud entries are excluded from in-house book totals. Detailed stats on these books can be obtained from marketing.', 14, 10, 'italic', 10);
    //addText(`${worker?.fullName || 'User'} - ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Mailing Stats`, 14, 10, 'italic', 10);

    // Save the PDF
    const fileName = `mailing_stats_${data.dateRange.start}_to_${data.dateRange.end}.pdf`;
    doc.save(fileName);
    
    showSuccessMessage('Mailing statistics report generated successfully!');
  };

  return (
    <Container>
      <Header>
        <Title>Mailing Statistics Report</Title>
        <Subtitle>Generate comprehensive mailing statistics for any date range</Subtitle>
      </Header>

      <FormSection>
        <DateInputGroup>
          <Label>Start Date:</Label>
          <DateInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            max={endDate || undefined}
          />
        </DateInputGroup>

        <DateInputGroup>
          <Label>End Date:</Label>
          <DateInput
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate || undefined}
            max={new Date().toISOString().split('T')[0]}
          />
        </DateInputGroup>

        <ButtonGroup>
          <GenerateButton 
            onClick={fetchReportData} 
            disabled={loading || !startDate || !endDate}
          >
            {loading ? 'Loading...' : 'Display Results'}
          </GenerateButton>
          
          <GenerateButton 
            onClick={generateReport} 
            disabled={loading || !startDate || !endDate || !reportData}
          >
            {loading ? 'Generating Report...' : 'Generate PDF Report'}
          </GenerateButton>
        </ButtonGroup>
      </FormSection>

      {reportData && (
        <PreviewSection>
          <PreviewTitle>Report Preview</PreviewTitle>
          <PreviewContent>
            <PreviewRow>
              <PreviewLabel>Date Range:</PreviewLabel>
              <PreviewValue>
                {new Date(reportData.dateRange.start).toLocaleDateString()} - {new Date(reportData.dateRange.end).toLocaleDateString()}
              </PreviewValue>
            </PreviewRow>
            <PreviewRow>
              <PreviewLabel>Total Brochures:</PreviewLabel>
              <PreviewValue>{reportData.totalBrochures}</PreviewValue>
            </PreviewRow>
            <PreviewRow>
              <PreviewLabel>Total Books (In-House):</PreviewLabel>
              <PreviewValue>{reportData.totalBooks}</PreviewValue>
            </PreviewRow>
            {reportData.externalBookCount > 0 && (
              <PreviewRow>
                <PreviewLabel>Excluded (Anchor / Give Cloud):</PreviewLabel>
                <PreviewValue>{reportData.externalBookCount}</PreviewValue>
              </PreviewRow>
            )}
            <PreviewRow>
              <PreviewLabel>Total Packages:</PreviewLabel>
              <PreviewValue>{reportData.totalPackages}</PreviewValue>
            </PreviewRow>
            <PreviewRow>
              <PreviewLabel>Canadian Packages:</PreviewLabel>
              <PreviewValue>{reportData.packageCounts.canada}</PreviewValue>
            </PreviewRow>
            <PreviewRow>
              <PreviewLabel>USA Packages:</PreviewLabel>
              <PreviewValue>{reportData.packageCounts.usa}</PreviewValue>
            </PreviewRow>
            <PreviewRow>
              <PreviewLabel>Materials Used:</PreviewLabel>
              <PreviewValue>
                Labels: {reportData.materialCounts.labels}, 
                Envelopes: {reportData.materialCounts.regularEnvelopes}, 
                Paper: {reportData.materialCounts.paperSheets} sheets
              </PreviewValue>
            </PreviewRow>
          </PreviewContent>
        </PreviewSection>
      )}
    </Container>
  );
};

export default MailingStatsReport;

// Styled Components
const Container = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Space Grotesk', sans-serif;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #106271;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #6B7280;
  font-size: 1rem;
`;

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
`;

const Label = styled.label`
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const DateInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: 'Space Grotesk', sans-serif;
  
  &:focus {
    outline: none;
    border-color: #106271;
    box-shadow: 0 0 0 3px rgba(16, 98, 113, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
`;

const GenerateButton = styled.button`
  background: #106271;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Space Grotesk', sans-serif;
  
  &:hover:not(:disabled) {
    background: #0F4C5C;
  }
  
  &:disabled {
    background: #9CA3AF;
    cursor: not-allowed;
  }
`;

const PreviewSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const PreviewTitle = styled.h3`
  color: #106271;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const PreviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #F3F4F6;
`;

const PreviewLabel = styled.span`
  color: #374151;
  font-weight: 500;
`;

const PreviewValue = styled.span`
  color: #106271;
  font-weight: 600;
`; 
